import React from 'react';

import { filesFromDataTransfer } from '~/components/cara-input/data-transfer-files';

export type ClipboardPasteHandler = (e: React.ClipboardEvent) => void;

/**
 * Clipboard paste handler for file attachments on the Cara compose input.
 *
 * When the clipboard carries files (e.g. a screenshot copied with Ctrl+C /
 * Cmd+C), intercepts the paste and routes them through the same attachment
 * queue as drag-and-drop and the native file picker. Plain-text pastes are
 * left to the browser so users can still paste into the message field.
 */
export function useClipboardPaste(args: {
  /** Called with pasted files when the clipboard contains at least one. */
  onPaste: (files: File[]) => void;
  /** When true, paste is ignored (e.g. while submitting). */
  disabled?: boolean;
}): ClipboardPasteHandler {
  const { onPaste, disabled } = args;

  const onPasteRef = React.useRef(onPaste);
  React.useEffect(() => {
    onPasteRef.current = onPaste;
  }, [onPaste]);

  return React.useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled) return;

      const files = filesFromDataTransfer(e.clipboardData);
      if (files.length === 0) return;

      e.preventDefault();
      onPasteRef.current(files);
    },
    [disabled]
  );
}
